import React, { useState, useMemo, useEffect } from 'react'
import { Select, Tooltip } from 'antd'
import { withNamespaces } from '@edulastic/localization'
import {
  EduButton,
  captureSentryException,
  notification,
} from '@edulastic/common'
import { segmentApi } from '@edulastic/api'
import { isEmpty, uniqBy } from 'lodash'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { withRouter, Link } from 'react-router-dom'
import {
  IconEditMetaData,
  IconInfo,
  IconBlueQuestionCircle,
  IconArrowRight,
} from '@edulastic/icons'

import {
  StyledApplyButton,
  StyledDivider,
  StyledEmptyStateContainer,
  StyledEmptyStateText,
  StyledErrorText,
  StyledGradientLoader,
  StyledLoadingContainer,
  StyledMetadataItem,
  StyledMetadataLabel,
  StyledMetadataLabelContainer,
  StyledMetadataSection,
  StyledRecommendationItem,
  StyledRecommendationLabel,
  StyledRecommendationList,
  StyledRecommendationListItem,
  StyledRecommendationsContainer,
  StyledRecommendationText,
  StyledSelect,
  StyledShowSuggestionsButton,
  StyledSpinnerWrapper,
  StyledStandardContainer,
  StyledStandardEditContainer,
  StyledStandardIdentifierContainer,
  StyledThinkingText,
  StyledStandardTooltipContainer,
  StyledStandardTooltipLabel,
  StyledStandardTooltipRow,
  StyledStandardSetDropdownContainer,
  StyledStandardSetSelect,
  StyledStandardSetLabel,
  StyledStandardIdentifier,
  StyledStandardDescription,
  StyledStandardLowConfidenceWarning,
  StyledMoreStandards,
  StyledNoStandardsSelected,
  StyledStandardTooltipWrapper,
  StyledStandardSetTooltipContent,
  StyledStandardSetInputContainer,
  StyledStandardSetTooltip,
  StyledAIContentFeedbackContainer,
} from './styled'
import { StyledRequired } from '../../Reports/subPages/dataWarehouseReports/GoalsAndInterventions/common/components/Form/styled-components'
import { getCurrentQuestionSelector } from '../../sharedDucks/questions'
import {
  getResolvedTestSubjectsSelector,
  getResolvedTestGradesSelector,
} from '../../TestPage/ducks'
import { DIFFICULTY_OPTIONS, DOK_OPTIONS } from '../constants/form'
import {
  getAIQuestionAssistanceState,
  generateAIQuestionMetadataAction,
  updateUserUpdatedDataAction,
  resetAIQuestionMetadataStateAction,
  setUserFeedbackAction,
  getUserFeedbackSelector,
} from '../ducks/questionAssistance'
import {
  getStandardCurriculumData,
  getStandardIdsToLog,
  getStandardSetOptions,
  getStandardTooltipItems,
  hasQuestionContentForMetadata,
  getConfidenceScoreForStandard,
  isStandardAddedToQuestion,
  resolveMetadataContext,
} from '../utils/AIQuestionAssistance'
import {
  getCurriculumsListSelector,
  standardsSelector,
  getDictionariesAlignmentsSelector,
} from '../../src/selectors/dictionaries'
import { getItemIdSelector } from '../../sharedDucks/itemDetail'

import {
  isAIQuestionAssistanceRecommendationsEnabledSelector,
  getAllInterestedCurriculumsSelector,
  getInterestedSubjectsSelector,
  getInterestedGradesSelector,
} from '../../src/selectors/user'
import StandardsModal from '../../../assessment/containers/QuestionMetadata/StandardsModal'
import { getDictStandardsForCurriculumAction } from '../../src/actions/dictionaries'
import AIContentFeedback from './AIContentFeedback'
import StandardsSuggestedModal from './StandardsSuggestedModal'

const segmentConfig = { attachUserData: true }
const LOW_CONFIDENCE_THRESHOLD = 0.7
const MAX_STANDARD_SUGGESTIONS = 3

const QuestionMetadataSection = ({
  t: i18translate,
  aiQuestionAssistanceState,
  currentQuestion,
  generateAIQuestionMetadata,
  onSaveSuggestedMetadata,
  allCurriculums,
  itemId = null,
  match,
  updateUserUpdatedData,
  isAIQuestionAssistanceRecommendationsEnabled,
  resetAIQuestionMetadataState,
  interestedCurriculums,
  interestedSubjects,
  interestedGrades,
  testSubjects,
  testGrades,
  dictionariesAlignments,
  getCurriculumStandards,
  curriculumStandardsLoading,
  setUserFeedback,
  userFeedback,
}) => {
  const testId = match?.params?.testId || null
  const { apiStatus, result, errorMessage } = aiQuestionAssistanceState || {}

  const [dokEdited, setDokEdited] = useState(false)
  const [difficultyEdited, setDifficultyEdited] = useState(false)
  const [selectedStandardSetId, setSelectedStandardSetId] = useState(null)
  const [
    showStandardsSelectionModal,
    setShowStandardsSelectionModal,
  ] = useState(false)
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false)

  const isLoading = apiStatus === 'INITIATED'
  const hasError = apiStatus === 'FAILED'

  const hasQuestionContent = hasQuestionContentForMetadata(currentQuestion)

  const { subjects: resolvedSubjects, grades: resolvedGrades } = useMemo(
    () =>
      resolveMetadataContext({
        testSubjects: testSubjects?.length > 0 ? testSubjects : [],
        testGrades: testGrades?.length > 0 ? testGrades : [],
        questionSubjects:
          currentQuestion?.subjects?.length > 0 ? currentQuestion.subjects : [],
        questionGrades:
          currentQuestion?.grades?.length > 0 ? currentQuestion.grades : [],
        dictionariesAlignments:
          dictionariesAlignments?.length > 0 ? dictionariesAlignments : [],
        interestedSubjects:
          interestedSubjects?.length > 0 ? interestedSubjects : [],
        interestedGrades: interestedGrades?.length > 0 ? interestedGrades : [],
      }),
    [
      testSubjects,
      testGrades,
      currentQuestion,
      dictionariesAlignments,
      interestedSubjects,
      interestedGrades,
    ]
  )

  const { standardSetOptions } = useMemo(
    () =>
      getStandardSetOptions({
        interestedCurriculums,
        interestedSubjects: resolvedSubjects,
      }),
    [interestedCurriculums, resolvedSubjects]
  )

  const flow = testId ? 'testAuthoring' : 'itemAuthoring'

  const suggestedMetaData = result?.suggestedMetaData
  const userUpdatedData = result?.userUpdatedData || {}
  const {
    dok: suggestedDok = '',
    difficulty: suggestedDifficulty = '',
    standards: suggestedStandards = [],
    suggested_edits: suggestedEdits = {},
    standardsMetadata: suggestedStandardsMetadata = [],
  } = suggestedMetaData || {}

  const {
    stimulus: suggestedStimulusEdit = '',
    options: suggestedOptionsEdits = [],
  } = suggestedEdits || {}

  const dok = userUpdatedData?.dok || suggestedDok || ''
  const difficulty = userUpdatedData?.difficulty || suggestedDifficulty || ''
  const hasStandardSetOptions = standardSetOptions?.length > 0

  const cappedSuggestions = suggestedStandards.slice(
    0,
    MAX_STANDARD_SUGGESTIONS
  )

  const selectedStandards = userUpdatedData?.standards || []

  const suggestedStandardIds = new Set(
    suggestedStandards.map((standard) => `${standard._id}`)
  )

  const standardsEdited = selectedStandards.some(
    (standard) => !suggestedStandardIds.has(`${standard._id}`)
  )

  useEffect(() => {
    if (hasStandardSetOptions) {
      const hasOnlyOneStandardSet = standardSetOptions?.length === 1
      if (hasOnlyOneStandardSet) {
        setSelectedStandardSetId(standardSetOptions[0]._id)
      }

      try {
        segmentApi.genericEventTrack(
          'AIQuestionMetadata:StandardSetInputDisplayed',
          {
            flow,
            itemId,
            testId,
            qid: currentQuestion?.id,
            sessionId: window?.sessionStorage?.kid,
            questionType: currentQuestion?.type,
            questionTitle: currentQuestion?.title,
            is_standard_set_preset: hasOnlyOneStandardSet,
            is_item_already_aligned: isStandardAddedToQuestion(currentQuestion),
          },
          segmentConfig
        )
      } catch (error) {
        captureSentryException(error, {
          errorMessage:
            'AI Question Assistance - METADATA: Error tracking StandardSetInputDisplayed event',
          itemId,
          questionId: currentQuestion?.id,
          sessionId: window?.sessionStorage?.kid,
        })
      }
    }
  }, [hasStandardSetOptions])

  const handleDokChange = (value) => {
    updateUserUpdatedData({ dok: value })
    setDokEdited(true)
  }

  const handleDifficultyChange = (value) => {
    updateUserUpdatedData({ difficulty: value })
    setDifficultyEdited(true)
  }

  const handleGenerateClick = () => {
    const curriculumId = selectedStandardSetId || null

    const subject = curriculumId
      ? interestedCurriculums?.find(
          (curriculum) => curriculum?._id === curriculumId
        )?.subject
      : null

    if (currentQuestion) {
      generateAIQuestionMetadata({
        question: currentQuestion,
        testId,
        selectedStandardSetId: curriculumId,
        subject,
        grades: resolvedGrades,
      })

      segmentApi.genericEventTrack(
        'AIQuestionMetadata:SuggestMetadataClick',
        {
          flow,
          itemId,
          testId,
          qid: currentQuestion?.id,
          sessionId: window?.sessionStorage?.kid,
          questionType: currentQuestion?.type,
          questionTitle: currentQuestion?.title,
        },
        segmentConfig
      )
    }
  }

  const difficultyOptions = DIFFICULTY_OPTIONS.filter(
    (option) => option.value !== ''
  )
  const dokOptions = DOK_OPTIONS.filter((option) => option.value !== '')

  const handleShowStandardsSelectionModal = (value) => {
    setShowStandardsSelectionModal(value)
  }

  const updateAlignment = (data = {}) => {
    updateUserUpdatedData({ standards: data?.eloStandards || [] })
    handleShowStandardsSelectionModal(false)

    if (showSuggestionsModal) {
      setShowSuggestionsModal(false)
    }
  }

  const handleUseSuggestion = (standard) => {
    const nextStandards = uniqBy([...selectedStandards, standard], '_id')
    updateUserUpdatedData({ standards: nextStandards })

    try {
      // Instrument only when the user picks a suggestion other than the primary
      // (highest-confidence) one, i.e. any index beyond 0.
      const suggestionIndex = cappedSuggestions.findIndex(
        (s) => `${s._id}` === `${standard._id}`
      )
      if (suggestionIndex > 0) {
        segmentApi.genericEventTrack(
          'AIQuestionMetadata:UseStandardSuggestionClick',
          {
            flow,
            itemId,
            testId,
            qid: currentQuestion?.id,
            sessionId: window?.sessionStorage?.kid,
            questionType: currentQuestion?.type,
            questionTitle: currentQuestion?.title,
            suggestionIndex: suggestionIndex + 1,
            suggestedStandardIds: getStandardIdsToLog(suggestedStandards),
          },
          segmentConfig
        )
      }
    } catch (error) {
      captureSentryException(error, {
        errorMessage:
          'AI Question Assistance - METADATA: Error tracking UseStandardSuggestionClick event',
        itemId,
        questionId: currentQuestion?.id,
        sessionId: window?.sessionStorage?.kid,
      })
    }
  }

  const handleRemoveStandard = (standard) => {
    const nextStandards = selectedStandards.filter(
      (s) => s._id !== standard._id
    )
    updateUserUpdatedData({ standards: nextStandards })
  }

  const handleBrowseStandards = () => {
    setShowStandardsSelectionModal(true)
  }

  const renderRecommendations = () => {
    if (
      isEmpty(suggestedEdits) ||
      (isEmpty(suggestedStimulusEdit) && isEmpty(suggestedOptionsEdits))
    ) {
      return null
    }

    const hasStimulusEdit = suggestedStimulusEdit?.trim?.()?.length > 0
    const hasOptionsEdits =
      Array.isArray(suggestedOptionsEdits) && suggestedOptionsEdits.length > 0

    return (
      <>
        <StyledRecommendationsContainer data-cy="pearAssistRecommendations">
          <StyledMetadataLabelContainer>
            <StyledMetadataLabel>Recommendations</StyledMetadataLabel>
            <Tooltip title="Improve readability of your question">
              <IconInfo
                width="14px"
                height="14px"
                fill="#919191"
                aria-hidden="true"
              />
            </Tooltip>
          </StyledMetadataLabelContainer>

          {hasStimulusEdit && (
            <StyledRecommendationItem>
              <StyledRecommendationLabel>Stimulus:</StyledRecommendationLabel>
              <StyledRecommendationText data-cy="pearAssistRecommendationStimulus">
                {suggestedStimulusEdit}
              </StyledRecommendationText>
            </StyledRecommendationItem>
          )}

          {hasOptionsEdits && (
            <StyledRecommendationItem>
              <StyledRecommendationLabel>Options:</StyledRecommendationLabel>
              <StyledRecommendationList data-cy="pearAssistRecommendationOptions">
                {suggestedOptionsEdits.map((optionEdit, index) => (
                  <StyledRecommendationListItem key={index}>
                    {optionEdit}
                  </StyledRecommendationListItem>
                ))}
              </StyledRecommendationList>
            </StyledRecommendationItem>
          )}
        </StyledRecommendationsContainer>
        <StyledDivider />
      </>
    )
  }

  const renderStandards = () => {
    if (!suggestedStandards || suggestedStandards?.length === 0) {
      return null
    }

    const defaultGrades = interestedGrades?.length > 0 ? interestedGrades : []

    const hasStandardSelection = selectedStandards.length > 0
    const firstStandard = hasStandardSelection ? selectedStandards[0] : null

    const firstStandardConfidence = firstStandard
      ? getConfidenceScoreForStandard(firstStandard, suggestedStandardsMetadata)
      : undefined
    const showLowConfidenceWarning =
      typeof firstStandardConfidence === 'number' &&
      firstStandardConfidence < LOW_CONFIDENCE_THRESHOLD

    const hasMultipleSelected = selectedStandards.length > 1

    const moreCount = hasMultipleSelected
      ? selectedStandards.length - 1
      : cappedSuggestions.length - 1

    const moreLinkLabel = hasMultipleSelected
      ? `and ${moreCount} more selected`
      : `and ${moreCount} more suggestion${moreCount > 1 ? 's' : ''}`

    const curriculum = getStandardCurriculumData(
      firstStandard || suggestedStandards[0],
      allCurriculums
    )

    const standardTooltipItems = getStandardTooltipItems(
      selectedStandards,
      curriculum
    )

    const {
      subject,
      _id: curriculumId,
      pdlCurriculumId,
      pdlAdoptYear,
      pdlCurriculumName = '',
      pdlSubjectLabel = '',
      pdlSubjectId,
      pdlGrades = [],
    } = curriculum || {}

    const pdlStandardProps = {
      pdlCurriculumId,
      pdlAdoptYear,
      pdlCurriculumName,
      pdlSubjectLabel,
      pdlSubjectId,
      pdlGrades,
      grades: defaultGrades,
      subject,
    }

    return (
      <StyledStandardContainer data-cy="pearAssistStandards">
        <StyledMetadataLabelContainer>
          <StyledMetadataLabel>Standards</StyledMetadataLabel>
          {hasStandardSelection && (
            <StyledStandardTooltipWrapper>
              <Tooltip
                title={
                  <StyledStandardTooltipContainer>
                    {standardTooltipItems.map((item) => (
                      <StyledStandardTooltipRow key={item.label}>
                        <StyledStandardTooltipLabel>
                          {item.label}:{' '}
                        </StyledStandardTooltipLabel>
                        {item.value}
                      </StyledStandardTooltipRow>
                    ))}
                  </StyledStandardTooltipContainer>
                }
              >
                <IconInfo
                  width="14px"
                  height="14px"
                  fill="#919191"
                  aria-hidden="true"
                />
              </Tooltip>
            </StyledStandardTooltipWrapper>
          )}
        </StyledMetadataLabelContainer>

        {hasStandardSelection ? (
          <>
            <StyledStandardIdentifierContainer>
              <StyledStandardIdentifier data-cy="pearAssistStandardIdentifier">
                {firstStandard.identifier}
              </StyledStandardIdentifier>
              <StyledStandardEditContainer
                data-cy="pearAssistStandardEdit"
                onClick={() => setShowSuggestionsModal(true)}
              >
                <IconEditMetaData aria-hidden="true" />
              </StyledStandardEditContainer>
            </StyledStandardIdentifierContainer>
            <StyledStandardDescription data-cy="pearAssistStandardDescription">
              {firstStandard.description}
            </StyledStandardDescription>
            {showLowConfidenceWarning && (
              <StyledStandardLowConfidenceWarning
                data-cy="pearAssistStandardLowConfidenceWarning"
                role="status"
              >
                Low confidence on standards suggested
              </StyledStandardLowConfidenceWarning>
            )}
            {moreCount > 0 && (
              <StyledMoreStandards
                type="button"
                data-cy="pearAssistStandardsMoreLink"
                onClick={() => setShowSuggestionsModal(true)}
              >
                {moreLinkLabel}
                <IconArrowRight color="#20845e" width={13} height={13} />
              </StyledMoreStandards>
            )}
          </>
        ) : (
          <>
            <StyledNoStandardsSelected data-cy="pearAssistNoStandardsSelected">
              No standards selected
            </StyledNoStandardsSelected>
            <StyledMoreStandards
              type="button"
              data-cy="pearAssistSeeSuggestionsLink"
              onClick={() => setShowSuggestionsModal(true)}
            >
              See {cappedSuggestions.length} standard suggestion
              {cappedSuggestions.length > 1 ? 's' : ''}
              <IconArrowRight color="#20845e" width={13} height={13} />
            </StyledMoreStandards>
          </>
        )}

        <StandardsSuggestedModal
          visible={showSuggestionsModal}
          onClose={() => setShowSuggestionsModal(false)}
          suggestions={cappedSuggestions}
          standardsMetadata={suggestedStandardsMetadata}
          selectedStandards={selectedStandards}
          onUseSuggestion={handleUseSuggestion}
          onRemoveStandard={handleRemoveStandard}
          onBrowseStandards={handleBrowseStandards}
          lowConfidenceThreshold={LOW_CONFIDENCE_THRESHOLD}
        />

        {showStandardsSelectionModal && (
          <StandardsModal
            t={i18translate}
            defaultSubject={subject}
            defaultGrades={defaultGrades}
            defaultStandards={selectedStandards}
            defaultStandard={{
              curriculum,
              id: curriculumId,
            }}
            pdlStandardProps={pdlStandardProps}
            visible={showStandardsSelectionModal}
            curriculums={allCurriculums}
            onApply={updateAlignment}
            setSubject={() => {}}
            onCancel={() => handleShowStandardsSelectionModal(false)}
            getCurriculumStandards={getCurriculumStandards}
            curriculumStandardsLoading={curriculumStandardsLoading}
            showAllInterestedCurriculums
          />
        )}
      </StyledStandardContainer>
    )
  }

  const handleApplyChanges = () => {
    if (isEmpty(suggestedMetaData)) {
      captureSentryException(
        new Error('No suggested metadata available to apply'),
        {
          errorMessage:
            'AI Question Assistance - METADATA: No suggested metadata found when applying AI metadata',
          itemId,
          questionId: currentQuestion?.id,
        }
      )

      notification({
        type: 'error',
        msg: 'An error occurred. Please try again later.',
      })

      resetAIQuestionMetadataState()
      return
    }

    if (onSaveSuggestedMetadata) {
      onSaveSuggestedMetadata()

      segmentApi.genericEventTrack(
        'AIQuestionMetadata:SaveSuggestionsClick',
        {
          flow,
          itemId,
          testId,
          qid: currentQuestion?.id,
          sessionId: window?.sessionStorage?.kid,
          questionType: currentQuestion?.type,
          questionTitle: currentQuestion?.title,
          dok,
          difficulty,
          standardIds: getStandardIdsToLog(userUpdatedData?.standards || []),
          dokEdited,
          difficultyEdited,
          standardsEdited,
        },
        segmentConfig
      )
    }
  }

  const renderMetadataContent = () => (
    <>
      <StyledMetadataSection data-cy="pearAssistMetadataSuccess">
        {isAIQuestionAssistanceRecommendationsEnabled &&
          renderRecommendations()}
        {renderStandards()}

        <StyledDivider />

        {dok && (
          <StyledMetadataItem>
            <StyledMetadataLabelContainer>
              <StyledMetadataLabel data-cy="pearAssistDokLabel">
                Depth of Knowledge
              </StyledMetadataLabel>
            </StyledMetadataLabelContainer>
            <StyledSelect
              data-cy="pearAssistDokSelect"
              value={dok}
              onChange={handleDokChange}
            >
              {dokOptions.map((option) => (
                <Select.Option key={option.label} value={option.label}>
                  {option.label}
                </Select.Option>
              ))}
            </StyledSelect>
          </StyledMetadataItem>
        )}

        <StyledDivider />

        {difficulty && (
          <StyledMetadataItem>
            <StyledMetadataLabelContainer>
              <StyledMetadataLabel>Difficulty Level</StyledMetadataLabel>
            </StyledMetadataLabelContainer>
            <StyledSelect
              data-cy="pearAssistDifficultySelect"
              value={difficulty}
              onChange={handleDifficultyChange}
            >
              {difficultyOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </StyledSelect>
          </StyledMetadataItem>
        )}
      </StyledMetadataSection>

      <StyledApplyButton>
        <EduButton
          data-cy="pearAssistSaveSuggestions"
          ml="0px"
          type="primary"
          onClick={handleApplyChanges}
          disabled={!currentQuestion}
        >
          Save Suggestions and Item
        </EduButton>
      </StyledApplyButton>

      <StyledAIContentFeedbackContainer data-cy="pearAssistAIContentFeedback">
        <AIContentFeedback
          initialRating={userFeedback || null}
          segmentEventName="AIContentQualityFeedback"
          segmentEventPayload={{
            featureName: 'QuestionMetadataGeneration',
            source: flow,
            itemId,
            testId,
            qid: currentQuestion?.id,
            sessionId: window?.sessionStorage?.kid,
          }}
          onSubmitFeedback={setUserFeedback}
        />
      </StyledAIContentFeedbackContainer>
    </>
  )

  if (isLoading) {
    return (
      <StyledLoadingContainer data-cy="pearAssistMetadataLoading">
        <StyledSpinnerWrapper>
          <StyledGradientLoader />
        </StyledSpinnerWrapper>
        <StyledThinkingText data-cy="pearAssistMetadataLoadingText">
          Reading question to generate standards and other details
        </StyledThinkingText>
      </StyledLoadingContainer>
    )
  }

  if (apiStatus === 'SUCCESS' && !isEmpty(suggestedMetaData)) {
    return renderMetadataContent()
  }

  if (hasError) {
    return (
      <StyledErrorText type="danger" data-cy="pearAssistMetadataError">
        {errorMessage || 'Failed to generate metadata suggestions'}
      </StyledErrorText>
    )
  }

  const isGenerateDisabled = !hasQuestionContent || !selectedStandardSetId

  return (
    <StyledEmptyStateContainer data-cy="pearAssistMetadataEmpty">
      <StyledEmptyStateText
        marginTop="4px"
        data-cy="pearAssistMetadataEmptyText"
      >
        Complete question authoring and click here to view AI suggested
        standards and meta data
      </StyledEmptyStateText>

      <StyledDivider />

      <StyledStandardSetDropdownContainer>
        <StyledStandardSetInputContainer>
          <StyledStandardSetLabel>
            <span>
              Standard Set<StyledRequired>*</StyledRequired>
            </span>
          </StyledStandardSetLabel>
          <StyledStandardSetTooltip
            title={
              <StyledStandardSetTooltipContent>
                The standard sets listed are based on your preferred standards.
                You can update these preferences from your profile page.
              </StyledStandardSetTooltipContent>
            }
          >
            <StyledStandardTooltipWrapper>
              <IconBlueQuestionCircle
                width="16px"
                height="16px"
                aria-hidden="true"
              />
            </StyledStandardTooltipWrapper>
          </StyledStandardSetTooltip>
        </StyledStandardSetInputContainer>
        {standardSetOptions?.length > 0 ? (
          <StyledStandardSetSelect
            data-cy="pearAssistStandardSetSelect"
            value={selectedStandardSetId ?? undefined}
            placeholder="Select"
            getPopupContainer={() => document.body}
            dropdownStyle={{ zIndex: 2000 }}
            onChange={(value) => setSelectedStandardSetId(value)}
          >
            {standardSetOptions.map((curriculum) => (
              <Select.Option key={curriculum._id} value={curriculum._id}>
                {curriculum.name || curriculum.curriculum}
              </Select.Option>
            ))}
          </StyledStandardSetSelect>
        ) : (
          <StyledEmptyStateText
            color="#262626"
            marginTop="0px"
            data-cy="pearAssistStandardSetEmptyText"
          >
            Please add standard set, grade, and subject in the{' '}
            <Link to="/author/profile">profile page</Link>.
          </StyledEmptyStateText>
        )}
      </StyledStandardSetDropdownContainer>

      <StyledShowSuggestionsButton>
        <EduButton
          data-cy="pearAssistSuggestMetadata"
          ml="0px"
          type="primary"
          onClick={handleGenerateClick}
          disabled={isGenerateDisabled}
        >
          Suggest Metadata
        </EduButton>
      </StyledShowSuggestionsButton>
    </StyledEmptyStateContainer>
  )
}

const enhance = compose(
  withNamespaces('assessment'),
  withRouter,
  connect(
    (state) => ({
      aiQuestionAssistanceState: getAIQuestionAssistanceState(state),
      currentQuestion: getCurrentQuestionSelector(state),
      allCurriculums: getCurriculumsListSelector(state),
      itemId: getItemIdSelector(state),
      isAIQuestionAssistanceRecommendationsEnabled: isAIQuestionAssistanceRecommendationsEnabledSelector(
        state
      ),
      interestedCurriculums: getAllInterestedCurriculumsSelector(state),
      interestedSubjects: getInterestedSubjectsSelector(state),
      interestedGrades: getInterestedGradesSelector(state),
      testSubjects: getResolvedTestSubjectsSelector(state),
      testGrades: getResolvedTestGradesSelector(state),
      dictionariesAlignments: getDictionariesAlignmentsSelector(state),
      curriculumStandardsLoading: standardsSelector(state)?.loading,
      userFeedback: getUserFeedbackSelector(state),
    }),
    {
      generateAIQuestionMetadata: generateAIQuestionMetadataAction,
      updateUserUpdatedData: updateUserUpdatedDataAction,
      resetAIQuestionMetadataState: resetAIQuestionMetadataStateAction,
      getCurriculumStandards: getDictStandardsForCurriculumAction,
      setUserFeedback: setUserFeedbackAction,
    }
  )
)
export default enhance(QuestionMetadataSection)
